import { useEffect, useMemo } from 'react'
import {
  BulletList,
  getTokenCssVar,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogNav,
  ModalDialogTitle,
  useScreenClass,
} from '@vp/swan'
import { findCountryByCode } from '../../../utils/country'
import { CountryListItem } from './CountryListItem'
import { getHeaderTracker } from '../../../tracking'
import { supportedCountries } from '../../../constants/country'
import { useTenantConfig } from '../../../context/TenantConfigContext'
import { deriveSupportedCountries } from '../../../utils/tenant-geoip-data'
import { useI18n } from '../../../context/TranslationContext'

interface CountrySelectorProps {
  isOpen: boolean
  handleClose: () => void
  locale: string
  tenant?: string
  selectedCountry: string
  countrySelectorDialogHeader?: string
}

export default function CountrySelector({ isOpen, handleClose, locale, tenant, selectedCountry, countrySelectorDialogHeader }: Readonly<CountrySelectorProps>) {
  const screen = useScreenClass()
  const isMobile = screen === 'xs'
  const tenantConfig = useTenantConfig()

  // Track country selector view when opened
  useEffect(() => {
    if (isOpen) {
      getHeaderTracker().trackFlyOutView('Country Selector', 'view')
    }
  }, [isOpen])

  const handleCloseWithTracking = () => {
    getHeaderTracker().trackFlyOutClick('Country Selector', 'close')
    handleClose()
  }

  const countryList = useMemo(() => {
    if (tenantConfig) {
      // Derive country list from tenant config
      const derivedCountries = deriveSupportedCountries(tenantConfig)
      const currentCountry = derivedCountries.find((c) => c.cloudflareCountryCode.toLowerCase() === selectedCountry.toLowerCase())
      if (currentCountry) {
        const rest = derivedCountries
          .filter((c) => c.cloudflareCountryCode !== currentCountry.cloudflareCountryCode)
          .sort((a, b) => a.text.localeCompare(b.text))
        return [currentCountry, ...rest]
      }
      return derivedCountries.sort((a, b) => a.text.localeCompare(b.text))
    }

    // Fallback to hardcoded VistaPrint data
    const currentCountry = findCountryByCode(selectedCountry)
    const rest = supportedCountries
      .filter(({ cloudflareCountryCode }) => cloudflareCountryCode !== currentCountry.cloudflareCountryCode)
      .sort((a, b) => a.text.localeCompare(b.text))
    return [currentCountry, ...rest]
  }, [selectedCountry, tenantConfig, locale])

  const desktopListStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px 40px' } as const
  const mobileListStyle = { display: 'flex', flexDirection: 'column', gap: getTokenCssVar('SwanSemSpace4') } as const

  return (
    <CountrySelectorModal isOpen={isOpen} handleClose={handleCloseWithTracking} countrySelectorDialogHeader={countrySelectorDialogHeader}>
      <BulletList role="list" data-bookends-locale={locale} data-bookends-tenant={tenant} style={isMobile ? mobileListStyle : desktopListStyle}>
        {countryList.map((country) => (
          <CountryListItem
            key={country.text}
            country={country}
            isSelected={country.cloudflareCountryCode.toLowerCase() === selectedCountry.toLowerCase()}
            locale={locale}
            tenant={tenant}
          />
        ))}
      </BulletList>
    </CountrySelectorModal>
  )
}

interface CountrySelectorModalProps {
  isOpen: boolean
  handleClose: () => void
  children: React.ReactNode
  countrySelectorDialogHeader?: string
}

function CountrySelectorModal({ isOpen, handleClose, children, countrySelectorDialogHeader }: Readonly<CountrySelectorModalProps>) {
  const { translate } = useI18n()
  const screen = useScreenClass()
  const isMobile = screen === 'xs'

  return (
    <ModalDialog standardMode isOpen={isOpen} id="countrySelectorDialog" onRequestDismiss={handleClose} takeOver={isMobile}>
      <ModalDialogContent aria-label={translate('countrySelector')}>
        <ModalDialogNav>
          {countrySelectorDialogHeader && <ModalDialogTitle display={{ sm: 'none' }}>{countrySelectorDialogHeader}</ModalDialogTitle>}
          <ModalDialogCloseButton ml="auto" />
        </ModalDialogNav>
        <ModalDialogBody>{children}</ModalDialogBody>
      </ModalDialogContent>
    </ModalDialog>
  )
}
